// packages/ui/src/contexts/sharedDialog.ts
//
// The app's ONE shared dialog (`UIContext.showDialog`): what a caller may ask of it, and which
// request owns it when several arrive.
//
// The slot shows one dialog, the last one requested. That is wrong for one case: a failure that
// reaches the screen twice. `requestAction` shows the cause it can see — the server no longer knows
// the action this page called, because the page is from an older release — and the caller then shows
// its own copy of the same failure, usually a generic "Unexpected error", which used to replace the one
// dialog that tells the operator what to do. So a request can be PINNED: a pinned dialog stays until
// the operator closes it, and a later unpinned request is dropped meanwhile. A later PINNED request
// still replaces it, so a caller can refine it (the manage-tour page's "the tour was saved, the photo
// changes were not").
//
// Pure (no React), so the precedence rule is unit-tested without rendering.

import type { ReactNode } from 'react';

import type { CommonKey } from '@tourtask/i18n/keys';
import type { GeneralDialogType } from '../components/GeneralDialog';

/** What `showDialog` takes. */
export interface ShowDialogArgs {
  contentCode: CommonKey;
  /**
   * Shown under the text: what it refers to, when a refusal names it — the rentals whose prices lock a
   * currency, say. Built by the caller (its own translated words), so the text stays one plain key.
   */
  extraContent?: ReactNode;
  titleCode?: CommonKey;
  type?: GeneralDialogType;
  /**
   * Keep this dialog until the operator closes it: a later request that is not pinned is dropped
   * while it is open (see the header). Only for a dialog that names the cause of everything that
   * follows — today, *Page out of date* and *Your account has been frozen*.
   */
  pinned?: boolean;
}

/** The part of the slot's state the precedence rule reads. */
export interface SharedDialogSlot {
  open: boolean;
  pinned: boolean;
}

/** The slot after `requested` arrives while it holds `current`. */
export function sharedDialogAfterRequest<TSlot extends SharedDialogSlot>(current: TSlot, requested: TSlot): TSlot {
  return current.open && current.pinned && !requested.pinned ? current : requested;
}

// The pinned requests today, *Page out of date* and *Your account has been frozen*, are
// `PAGE_OUT_OF_DATE_DIALOG` in `@tourtask/utils/client/pageOutOfDateDialog` and
// `BUSINESS_FROZEN_DIALOG` in `@tourtask/utils/client/businessFrozenDialog` — next to
// `requestAction`, which shows them.
